import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  alpha,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { campaignApi } from '../services/campaignApi';
import { fetchPhoneChannels } from './PhoneNumberSelect';

interface Campaign {
  id: string | number;
  name: string;
  description: string;
  completedCalls: number;
  scheduledCalls: number;
  isLaunched?: boolean;
}

interface CampaignsProps {
  onCreateCampaign: () => void;
  onEditCampaign?: (campaignId: string) => void;
  onViewCampaign?: (campaignId: string) => void;
  showTitle?: boolean;
}

type LaunchFilter = 'all' | 'launched' | 'unlaunched';

const Campaigns: React.FC<CampaignsProps> = ({ onCreateCampaign, onEditCampaign, onViewCampaign, showTitle = false }) => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [allCampaigns, setAllCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [launchFilter, setLaunchFilter] = useState<LaunchFilter>('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [campaignToDelete, setCampaignToDelete] = useState<Campaign | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await campaignApi.getCampaigns();
        const campaignsArray = Array.isArray(data) ? data : [];
        setAllCampaigns(campaignsArray);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch campaigns');
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
    
    // Preload phone channels when campaigns page loads
    fetchPhoneChannels().catch((err) => {
      console.error('Failed to preload phone channels:', err);
    });
  }, []);

  // Filter campaigns based on launch status
  useEffect(() => {
    if (launchFilter === 'all') {
      setCampaigns(allCampaigns);
    } else if (launchFilter === 'launched') {
      setCampaigns(allCampaigns.filter(c => c.isLaunched === true));
    } else if (launchFilter === 'unlaunched') {
      setCampaigns(allCampaigns.filter(c => c.isLaunched !== true));
    }
  }, [launchFilter, allCampaigns]);

  const handleCreateCampaign = () => {
    onCreateCampaign();
  };

  const handleCampaignClick = (campaign: Campaign) => {
    // If campaign is launched, view details; otherwise, edit it
    if (campaign.isLaunched && onViewCampaign) {
      onViewCampaign(String(campaign.id));
    } else if (onEditCampaign) {
      onEditCampaign(String(campaign.id));
    }
  };

  const handleEditClick = (e: React.MouseEvent, campaignId: string | number) => {
    e.stopPropagation();
    if (onEditCampaign) {
      onEditCampaign(String(campaignId));
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, campaign: Campaign) => {
    e.stopPropagation();
    setCampaignToDelete(campaign);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!campaignToDelete) return;
    
    setDeleting(true);
    setError(null);
    try {
      await campaignApi.deleteCampaign(String(campaignToDelete.id));
      // Refresh campaigns list
      const data = await campaignApi.getCampaigns();
      const campaignsArray = Array.isArray(data) ? data : [];
      setAllCampaigns(campaignsArray);
      setDeleteDialogOpen(false);
      setCampaignToDelete(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete campaign');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setCampaignToDelete(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {showTitle && (
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h2">Campaigns</Typography>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Status</InputLabel>
            <Select
              value={launchFilter}
              label="Filter by Status"
              onChange={(e) => setLaunchFilter(e.target.value as LaunchFilter)}
            >
              <MenuItem value="all">All Campaigns</MenuItem>
              <MenuItem value="launched">Launched Only</MenuItem>
              <MenuItem value="unlaunched">Unlaunched Only</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}
      {!showTitle && (
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Status</InputLabel>
            <Select
              value={launchFilter}
              label="Filter by Status"
              onChange={(e) => setLaunchFilter(e.target.value as LaunchFilter)}
            >
              <MenuItem value="all">All Campaigns</MenuItem>
              <MenuItem value="launched">Launched Only</MenuItem>
              <MenuItem value="unlaunched">Unlaunched Only</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}
      <Grid container spacing={3}>
        {/* Create Campaign Card */}
        <Grid item xs={12} sm={6} md={4}>
          <Paper
            sx={{
              height: 240,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              border: '2px dashed',
              borderColor: alpha('#1e293b', 0.2),
              bgcolor: alpha('#1e293b', 0.02),
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: '#1e293b',
                bgcolor: alpha('#1e293b', 0.04),
                transform: 'translateY(-4px)',
              },
            }}
            onClick={handleCreateCampaign}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: alpha('#1e293b', 0.08),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <AddIcon sx={{ fontSize: 32, color: '#1e293b' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
              Create a Campaign
            </Typography>
          </Paper>
        </Grid>

        {/* Campaign Cards */}
        {campaigns.map((campaign) => (
          <Grid item xs={12} sm={6} md={4} key={campaign.id}>
            <Paper
              sx={{
                minHeight: 260,
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                position: 'relative',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 4px 12px ${alpha('#000', 0.1)}`,
                },
              }}
              onClick={() => handleCampaignClick(campaign)}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: '#1e293b',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    pr: 1,
                  }}
                >
                  {campaign.name}
                </Typography>
                {!campaign.isLaunched && (
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {onEditCampaign && (
                      <IconButton
                        size="small"
                        onClick={(e) => handleEditClick(e, campaign.id)}
                        sx={{
                          color: alpha('#000', 0.5),
                          '&:hover': {
                            color: '#1e293b',
                            backgroundColor: alpha('#000', 0.04),
                          },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={(e) => handleDeleteClick(e, campaign)}
                      sx={{
                        color: alpha('#d32f2f', 0.7),
                        '&:hover': {
                          color: '#d32f2f',
                          backgroundColor: alpha('#d32f2f', 0.08),
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <Chip
                  label={campaign.isLaunched ? 'Launched' : 'Draft'}
                  size="small"
                  color={campaign.isLaunched ? 'success' : 'default'}
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.75rem',
                  }}
                />
              </Box>

              <Typography
                variant="body2"
                sx={{
                  color: alpha('#000', 0.6),
                  mb: 2,
                  flex: 1,
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {campaign.description || 'No description'}
              </Typography>

              <Box sx={{ mt: 'auto' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 1.5,
                  }}
                >
                  <Box>
                    <Typography variant="caption" sx={{ color: alpha('#000', 0.5) }}>
                      Completed Calls
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                      {campaign.completedCalls || 0}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" sx={{ color: alpha('#000', 0.5) }}>
                      Scheduled Calls
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                      {campaign.scheduledCalls || 0}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Empty State - shown when there are no campaigns */}
      {campaigns.length === 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 300,
            mt: 4,
          }}
        >
          <Box sx={{ textAlign: 'center', maxWidth: 400 }}>
            <Typography variant="h6" sx={{ mb: 1, color: alpha('#000', 0.6) }}>
              No campaigns yet
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: alpha('#000', 0.5) }}>
              Create your first campaign to start organizing your outbound calls
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateCampaign}
            >
              Create Campaign
            </Button>
          </Box>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="delete-dialog-title" sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon sx={{ color: 'error.main' }} />
            <Typography variant="h6" component="span">
              Delete Campaign
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description" sx={{ mb: 2 }}>
            Are you sure you want to delete the campaign <strong>"{campaignToDelete?.name}"</strong>?
          </DialogContentText>
          <Alert severity="error" sx={{ mb: 2 }}>
            This action cannot be undone. All campaign data, contact lists, and contacts will be permanently deleted.
          </Alert>
          {campaignToDelete && campaignToDelete.scheduledCalls > 0 && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Warning: This campaign has {campaignToDelete.scheduledCalls} scheduled call(s).
              </Typography>
              <Typography variant="body2">
                Deleting this campaign will also cancel all scheduled calls.
              </Typography>
            </Alert>
          )}
          {campaignToDelete && campaignToDelete.completedCalls > 0 && (
            <Alert severity="info" sx={{ mt: 1 }}>
              This campaign has {campaignToDelete.completedCalls} completed call(s). Historical call data will be preserved.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleDeleteCancel} 
            disabled={deleting}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete Campaign'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Campaigns;
